import React, { useState } from "react";
import api from "@/lib/api";
import { money } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { toast } from "sonner";
import { MapPin, CloudRain, Fuel, Percent, BedDouble, Loader2, Wand2, Truck, Route, Gauge, SlidersHorizontal, X } from "lucide-react";

const MON = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const pct = (f) => `${Math.round((f ?? 0) * 100)}%`;

export default function LocationPanel({ bid, updateBid, computed, readOnly }) {
  const saved = bid.location_profile || null;
  const [state, setState] = useState(bid.state || "");
  const [county, setCounty] = useState(bid.location_county || bid.county || "");
  const [profile, setProfile] = useState(saved);
  const [loading, setLoading] = useState(false);
  const lastKey = React.useRef(saved ? `${(bid.state || "").toUpperCase()}|${bid.location_county || bid.county || ""}` : "");

  const weatherPatch = (prof) => {
    const f = prof?.weather_blended_factor;
    if (!f) return {};
    const next = (bid.scope_lines || []).map((l) => (l.inputs ? { ...l, inputs: { ...l.inputs, weather_factor: f } } : l));
    return { scope_lines: next, weather_factor: f };
  };

  const fetchIntel = async (refresh = false) => {
    if (!state.trim()) { if (refresh) toast.error("No location set on the CRM lead yet."); return; }
    const key = `${state.trim().toUpperCase()}|${county.trim()}`;
    if (!refresh && key === lastKey.current && profile) return;   // already pulled for this location
    setLoading(true);
    try {
      const r = await api.post("/location/intel", { state: state.trim(), county: county.trim(), refresh });
      setProfile(r.data);
      lastKey.current = key;
      (r.data.warnings || []).forEach((w) => toast.warning(w, { duration: 9000 }));
      // Only a person's click writes the profile onto the bid (D1/D2); opening the tab never re-prices anything.
      if (!readOnly && refresh) {
        try { await updateBid({ location_profile: r.data, state: state.trim().toUpperCase(), location_county: county.trim(), ...weatherPatch(r.data) }); }
        catch (e) { if (e?.response?.status !== 409) throw e; }
      }
      if (refresh) toast.success(`Location data refreshed — weather ${pct(r.data.weather_blended_factor)} applied to all scopes`);
    } catch (e) { toast.error(e.response?.data?.detail?.message || e.response?.data?.detail || "Could not fetch location data"); }
    finally { setLoading(false); }
  };

  // Location is inherited from the CRM lead — show the saved profile, or a preview, without writing to the bid.
  React.useEffect(() => {
    if (!saved && state.trim() && !profile && !loading) fetchIntel(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const w = profile?.weather;
  const maxLost = w ? Math.max(...w.months.map((m) => m.lost_days), 0.1) : 1;

  return (
    <div className="p-6 max-w-[1800px] w-full mx-auto" data-testid="location-panel">
      <div className="flex items-end gap-3 flex-wrap mb-4">
        <div>
          <h2 className="text-sm font-semibold uppercase tracking-wider text-slate-600">Productivity &amp; Conditions</h2>
          <p className="text-xs text-slate-500 mt-0.5">Location is set on the CRM lead and inherited here. Weather, diesel &amp; tax pull automatically, rolling up with your site conditions to one effective productivity that feeds pricing.</p>
        </div>
        <div className="flex-1" />
        <div className="flex items-end gap-3">
          <div className="text-right">
            <div className="text-[10px] uppercase tracking-wide text-slate-400">Location (from CRM)</div>
            <div className="text-sm font-semibold text-slate-800" data-testid="loc-inherited">{state ? state.toUpperCase() : "—"}{county ? ` · ${county}` : ""}</div>
          </div>
          {loading ? (
            <Button disabled className="bg-[#003E87] h-9"><Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> Loading</Button>
          ) : profile ? (
            <Button variant="outline" onClick={() => fetchIntel(true)} disabled={readOnly} className="h-9" data-testid="loc-refresh">Refresh</Button>
          ) : (
            <Button onClick={() => fetchIntel(false)} disabled={!state.trim()} className="bg-[#003E87] hover:bg-[#00305F] h-9" data-testid="loc-fetch">
              <MapPin className="w-4 h-4 mr-1.5" /> Pull data
            </Button>
          )}
        </div>
      </div>

      {!state && (
        <Card className="p-6 border-amber-300 bg-amber-50 mb-4" data-testid="loc-no-crm-location">
          <div className="flex items-center gap-2 text-amber-800 font-semibold text-sm"><MapPin className="w-4 h-4" /> No location set on the CRM lead</div>
          <p className="text-xs text-amber-700 mt-1">Set the project's State &amp; County on the lead in the CRM Pipeline — it flows here automatically and drives weather, diesel and sales tax.</p>
        </Card>
      )}

      <ProductivityCard bid={bid} updateBid={updateBid} readOnly={readOnly} weatherFactor={profile?.weather_blended_factor} weatherAvailable={!!w} />
      <StandDownCard bid={bid} updateBid={updateBid} readOnly={readOnly} />

      {state && !profile && !loading && (
        <Card className="p-6 border-amber-300 bg-amber-50" data-testid="loc-no-location">
          <div className="flex items-center gap-2 text-amber-800 font-semibold text-sm"><CloudRain className="w-4 h-4" /> Site data not pulled yet — productivity is assumed 100%</div>
          <p className="text-xs text-amber-700 mt-1">Weather, diesel and sales tax load automatically from the inherited CRM location. If they didn't, click <span className="font-semibold">Pull data</span> above.</p>
        </Card>
      )}

      {profile && (
        <div className="space-y-4">
          {profile.geo && <div className="text-xs text-slate-500">Resolved to <span className="font-medium text-slate-700">{profile.geo.resolved}</span> · {profile.geo.source}{profile.geo.approximate ? <span className="text-amber-600 ml-1" data-testid="loc-approx-note">· using regional estimate (couldn't pin the exact county)</span> : ""}</div>}

          {/* Data cards */}
          <div className="grid sm:grid-cols-2 gap-3">
            <Card className="p-4" data-testid="loc-diesel">
              <div className="flex items-center gap-2 text-slate-500 text-xs mb-1"><Fuel className="w-4 h-4 text-[#003E87]" /> Diesel</div>
              <div className="text-xl font-bold font-mono text-slate-900">${profile.diesel?.price?.toFixed(3)}<span className="text-xs text-slate-400 font-normal">/gal</span></div>
              <div className="text-[10px] text-slate-400 mt-1">{profile.diesel?.source}{profile.diesel?.period ? ` · ${profile.diesel.period}` : ""}</div>
            </Card>
            <TaxCard bid={bid} profile={profile} updateBid={updateBid} readOnly={readOnly} />
          </div>

          {/* Weather */}
          {w ? (
            <Card className="p-5" data-testid="loc-weather">
              <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
                <div className="flex items-center gap-2 text-sm font-semibold text-slate-700"><CloudRain className="w-4 h-4 text-[#003E87]" /> Weather productivity — {w.years}-yr normals</div>
                <div className="flex items-center gap-3">
                  <div className="text-xs text-slate-500">Blended factor <span className="font-mono font-bold text-slate-900" data-testid="loc-weather-blended">{pct(profile.weather_blended_factor)}</span></div>
                  <span className="text-[11px] text-emerald-600 font-medium" data-testid="loc-weather-applied">✓ Auto-applied to all scopes</span>
                </div>
              </div>
              {/* month bars */}
              <div className="grid grid-cols-12 gap-1.5 items-end h-28 mb-2">
                {w.months.map((m) => (
                  <div key={m.month} className="flex flex-col items-center justify-end h-full" title={`${MON[m.month - 1]}: ${pct(m.factor)} (${m.lost_days} lost days/mo)`}>
                    <div className="w-full rounded-t" style={{ height: `${Math.round((m.lost_days / maxLost) * 100)}%`, minHeight: 2, background: m.factor >= 0.9 ? "#16a34a" : m.factor >= 0.75 ? "#F4C400" : "#E5484D" }} data-testid={`loc-month-bar-${m.month}`} />
                    <div className="text-[9px] text-slate-400 mt-1">{MON[m.month - 1]}</div>
                    <div className="text-[9px] font-mono text-slate-600">{pct(m.factor)}</div>
                  </div>
                ))}
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-[11px] mt-2">
                  <thead><tr className="text-slate-400 border-b border-slate-100 text-left">
                    <th className="py-1">Month</th><th>Rain d</th><th>Washout</th><th>Snow</th><th>Heat</th><th>Cold</th><th>Wind</th><th className="text-right">Lost d</th><th className="text-right">Factor</th></tr></thead>
                  <tbody>
                    {w.months.map((m) => (
                      <tr key={m.month} className="border-b border-slate-50">
                        <td className="py-1 font-medium text-slate-600">{MON[m.month - 1]}</td>
                        <td>{m.rain}</td><td>{m.washout}</td><td>{m.snow}</td><td>{m.heat}</td><td>{m.cold}</td><td>{m.wind}</td>
                        <td className="text-right font-mono">{m.lost_days}</td>
                        <td className="text-right font-mono font-semibold">{pct(m.factor)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <p className="text-[10px] text-slate-400 mt-2">{w.source}. Bars show expected lost field-days/month; color = productivity. Applying pushes the blended factor into every calculator scope's weather input (still overridable per line).</p>
            </Card>
          ) : (
            <Card className="p-5 text-sm text-slate-500" data-testid="loc-weather">Weather normals unavailable for this location — check the county spelling and try Refresh.</Card>
          )}
        </div>
      )}
    </div>
  );
}

const MOB_FIELDS = [
  ["crew_trucks", "Crew trucks"], ["equipment_loads", "Equipment loads (lowboy)"], ["headcount", "Crew headcount"],
  ["round_trips", "Round trips"], ["truck_mpg", "Truck mpg"], ["lowboy_rate", "Lowboy $/mi"],
  ["driver_wage", "Driver $/hr"], ["travel_days", "Travel days (per-diem)"],
];

function MobilizationCard({ bid, updateBid, readOnly, site, diesel, perDiem, managerTrucks = 0 }) {
  const saved = bid.mobilization || null;
  const [hq, setHq] = useState(saved?.hq || "");
  const [p, setP] = useState({ crew_trucks: 4, equipment_loads: 6, headcount: 20, round_trips: 1, truck_mpg: 12, lowboy_rate: 5.5, driver_wage: 45, travel_days: 2, ...(saved?.params || {}) });
  const [res, setRes] = useState(saved && saved.status === "priced" ? saved : null);
  const [loading, setLoading] = useState(false);
  const applied = !!bid.mobilization;
  const setF = (k, v) => setP((s) => ({ ...s, [k]: v === "" ? "" : Number(v) }));

  const calc = async () => {
    if (!hq.trim()) { toast.error("Enter a crew HQ (e.g. 'Midland, TX')."); return; }
    setLoading(true);
    try {
      const r = await api.post("/location/mobilization", {
        hq: hq.trim(),
        site: { lat: site?.lat, lon: site?.lon, resolved: site?.resolved, state: bid.state, county: bid.location_county },
        params: { ...p, crew_trucks: (Number(p.crew_trucks) || 0) + Number(managerTrucks || 0) }, diesel_price: diesel, per_diem_daily: perDiem,
      });
      if (r.data.status !== "priced") { toast.error(r.data.warnings?.[0] || "Could not compute mobilization"); setRes(null); }
      else { setRes(r.data); toast.success(`Mobilization ${money(r.data.total)} · ${r.data.distance_mi} mi`); }
    } catch (e) { toast.error("Could not compute mobilization"); }
    finally { setLoading(false); }
  };
  const addToBid = () => { updateBid({ mobilization: { ...res, hq, params: p } }); toast.success("Mobilization added to the estimate total"); };
  const removeFromBid = () => { updateBid({ mobilization: null }); toast.success("Mobilization removed from the total"); };

  return (
    <Card className="p-5" data-testid="loc-mobilization">
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <div className="flex items-center gap-2 text-sm font-semibold text-slate-700"><Truck className="w-4 h-4 text-[#003E87]" /> Mobilization (auto)
          {perDiem != null && <span className="text-[10px] font-normal text-slate-400" data-testid="mob-perdiem-ref"><BedDouble className="w-3 h-3 inline mb-0.5 mr-0.5" />per-diem {money(perDiem)}/day (sourced)</span>}
        </div>
        {res && <div className="text-xs text-slate-500 flex items-center gap-1"><Route className="w-3.5 h-3.5" /> {res.distance_mi} mi · {res.drive_hours} h each way</div>}
      </div>
      <div className="flex items-end gap-2 flex-wrap mb-3">
        <div className="flex-1 min-w-[180px]">
          <label className="text-[10px] uppercase text-slate-400">Crew HQ (city, ST)</label>
          <Input value={hq} onChange={(e) => setHq(e.target.value)} placeholder="Midland, TX" className="h-9" data-testid="mob-hq" disabled={readOnly} />
        </div>
        <Button onClick={calc} disabled={loading || readOnly} className="bg-[#003E87] hover:bg-[#00305F] h-9" data-testid="mob-calc">
          {loading ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Truck className="w-4 h-4 mr-1.5" />} Calculate
        </Button>
      </div>
      {managerTrucks > 0 && (
        <div className="text-[11px] text-emerald-700 bg-emerald-50 border border-emerald-100 rounded px-2.5 py-1.5 mb-3" data-testid="mob-manager-trucks">
          +{managerTrucks} pickup truck{managerTrucks > 1 ? "s" : ""} from Management included automatically in the truck count.
        </div>
      )}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-3">
        {MOB_FIELDS.map(([k, label]) => (
          <div key={k}>
            <label className="text-[10px] uppercase text-slate-400">{label}</label>
            <Input type="number" step="any" value={p[k]} onChange={(e) => setF(k, e.target.value)} className="h-8 text-xs font-mono" data-testid={`mob-${k}`} disabled={readOnly} />
          </div>
        ))}
      </div>
      {res && (
        <div className="border border-slate-200 rounded-lg overflow-hidden" data-testid="mob-result">
          {res.line_items.map((it, i) => (
            <div key={i} className="flex items-center justify-between px-3 py-2 border-b border-slate-50 text-xs">
              <div><div className="font-medium text-slate-700">{it.label}</div><div className="text-[10px] text-slate-400">{it.detail}</div></div>
              <div className="font-mono text-slate-800">{money(it.amount)}</div>
            </div>
          ))}
          <div className="flex items-center justify-between px-3 py-2 bg-slate-50">
            <span className="text-xs font-semibold text-slate-600">Mobilization total</span>
            <span className="text-sm font-mono font-bold text-black" data-testid="mob-total">{money(res.total)}</span>
          </div>
        </div>
      )}
      {res && !readOnly && (
        <div className="flex items-center gap-2 mt-3">
          {!applied
            ? <Button size="sm" onClick={addToBid} className="bg-[#F4C400] text-black hover:brightness-95" data-testid="mob-apply">Add to estimate total</Button>
            : <><span className="text-xs text-emerald-600 font-medium" data-testid="mob-applied">✓ Included in the estimate total</span>
                <Button size="sm" variant="outline" onClick={removeFromBid} data-testid="mob-remove">Remove</Button></>}
        </div>
      )}
      <p className="text-[10px] text-slate-400 mt-2">Distance is real driving distance (OSRM) from your HQ to the site. Fuel uses the live diesel price above; per-diem uses the suggested daily rate. Every assumption is editable.</p>
    </Card>
  );
}

// Predefined project-wide site slowdowns. Everything is add-as-needed and starts at 0%.
const SLOWDOWN_TYPES = [
  { key: "shift", label: "Shift length", note: "Fatigue on 6×10s or 7×12s vs a standard 5×8" },
  { key: "congestion", label: "Congestion / stacking", note: "Crews or trades sharing the same work area" },
  { key: "access", label: "Site access", note: "In-site travel, soft ground, gates, escorts" },
  { key: "learning", label: "Learning curve", note: "Ramp-up before the crew hits steady pace" },
  { key: "terrain", label: "Terrain / topography", note: "Slopes and grading slow equipment and crews" },
  { key: "rocky_soil", label: "Hard / rocky soil", note: "Refusal and pre-drilling slow piles and trenching" },
  { key: "water_table", label: "High water table", note: "Dewatering, mud and unstable excavations" },
  { key: "remote", label: "Remote access", note: "Long haul to site, limited services and support" },
  { key: "dust", label: "Dust control", note: "Watering, wind stops and visibility limits" },
  { key: "heat", label: "Heat", note: "Work/rest cycles and heat stress in hot months" },
  { key: "cold", label: "Cold", note: "Frozen ground, bundled crews and slow equipment starts" },
  { key: "wind", label: "Wind", note: "Crane/lift stops and material-handling limits" },
  { key: "environmental", label: "Environmental mitigation", note: "Wetlands, habitat windows, setbacks, SWPPP" },
  { key: "archaeological", label: "Archaeological / cultural", note: "Monitoring, avoidance areas and stop-work finds" },
  { key: "haul_roads", label: "Poor haul roads", note: "Slow internal transport, spotting and recovery" },
];

function ProductivityCard({ bid, updateBid, readOnly, weatherFactor, weatherAvailable }) {
  const saved = (bid.conditions?.factors || []).map((f) => ({ ...f, pct: Number(f.pct) || 0 }));
  const hoursCarryWx = !!bid.work_basis?.hours_include_weather;
  const setHoursCarryWx = (v) => updateBid({ work_basis: { ...(bid.work_basis || {}), hours_include_weather: v } });
  const commit = (next) => updateBid({ conditions: { factors: next } });
  const setPct = (key, val) => commit(saved.map((f) => (f.key === key ? { ...f, pct: val === "" ? 0 : Number(val) } : f)));
  const removeFactor = (key) => commit(saved.filter((f) => f.key !== key));
  const addType = (key) => {
    if (!key) return;
    if (key === "__custom") {
      const name = window.prompt("Name this site slowdown (e.g. Overhead power lines):");
      if (!name || !name.trim()) return;
      commit([...saved, { key: `custom_${Date.now()}`, label: name.trim(), note: "Custom project slowdown", pct: 0 }]);
      return;
    }
    if (saved.some((f) => f.key === key)) return;
    const t = SLOWDOWN_TYPES.find((x) => x.key === key);
    if (!t) return;
    commit([...saved, { key: t.key, label: t.label, note: t.note, pct: 0 }]);
  };
  const available = SLOWDOWN_TYPES.filter((t) => !saved.some((f) => f.key === t.key));
  const totalSlow = saved.reduce((s, f) => s + (Number(f.pct) || 0), 0);
  const wf = hoursCarryWx ? 1 : (weatherFactor != null ? Number(weatherFactor) : 1);
  const eff = Math.max(0.05, wf * (1 - totalSlow / 100));

  return (
    <Card className="p-5 mb-4 border-[#003E87]/20 bg-[#003E87]/[0.02]" data-testid="productivity-card">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-2 text-sm font-semibold text-slate-700"><Gauge className="w-4 h-4 text-[#003E87]" /> Effective productivity</div>
        <div className="text-right">
          <div className="text-3xl font-bold font-mono text-[#003E87]" data-testid="productivity-effective">{Math.round(eff * 100)}%</div>
          <div className="text-[11px] text-slate-500 font-mono">
            weather <span className="font-semibold">{Math.round(wf * 100)}%</span>{hoursCarryWx ? <span className="text-emerald-600"> (in the hours)</span> : !weatherAvailable ? <span className="text-amber-600"> (assumed — no model)</span> : ""} × (1 − slowdown <span className="font-semibold" data-testid="productivity-slowdown-total">{totalSlow}%</span>)
          </div>
        </div>
      </div>
      <label className="flex items-start gap-2 mt-3 mb-2 rounded-lg border border-slate-200 bg-white px-3 py-2 cursor-pointer" data-testid="hours-include-weather-row">
        <input type="checkbox" className="mt-0.5" checked={hoursCarryWx} disabled={readOnly} onChange={(e) => setHoursCarryWx(e.target.checked)} data-testid="hours-include-weather" />
        <span className="text-xs text-slate-700"><b>The hours already carry weather.</b> <span className="text-slate-500">Turn this on when the line hours or production rates were set with weather in them — no crew duration, per-diem or equipment days are stretched by the site weather factor. Carry stand-down days as their own block below instead.</span></span>
      </label>
      <p className="text-[11px] text-slate-500 mt-2 mb-3">This is the pace every scope inherits unless it sets its own slowdown. Weather comes from the site model below; add the site slowdowns that apply — everything starts at 0%.</p>

      <div className="flex items-center justify-between gap-2 mb-2 flex-wrap">
        <div className="flex items-center gap-2 text-[10px] uppercase tracking-wide text-slate-400"><SlidersHorizontal className="w-3.5 h-3.5" /> Site slowdowns (project-wide)</div>
        {!readOnly && (
          <select value="" onChange={(e) => { addType(e.target.value); e.target.value = ""; }} data-testid="condition-add-select"
            className="text-[11px] border border-slate-200 rounded-lg px-2 py-1.5 bg-white text-slate-700 focus:border-[#003E87] outline-none">
            <option value="">＋ Add a slowdown…</option>
            {available.map((t) => <option key={t.key} value={t.key}>{t.label}</option>)}
            <option value="__custom">＋ Custom slowdown…</option>
          </select>
        )}
      </div>

      {saved.length === 0 ? (
        <div className="rounded-lg border border-dashed border-slate-200 bg-white/60 px-4 py-6 text-center" data-testid="condition-empty">
          <div className="text-xs text-slate-500">No site slowdowns yet.</div>
          <div className="text-[11px] text-slate-400 mt-0.5">{readOnly ? "None added." : "Use “Add a slowdown” to include terrain, rocky soil, remote access, heat and more — each starts at 0%."}</div>
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2">
          {saved.map((f) => (
            <div key={f.key} className="rounded-lg border border-slate-200 bg-white p-3" data-testid={`condition-factor-${f.key}`}>
              <div className="flex items-center justify-between gap-2">
                <span className="text-xs font-medium text-slate-700 truncate pr-1">{f.label}</span>
                <div className="flex items-center gap-0.5 shrink-0">
                  <Input type="number" step="any" value={f.pct || ""} onChange={(e) => setPct(f.key, e.target.value)} disabled={readOnly}
                    className="h-7 w-16 text-xs text-right font-mono" placeholder="0" data-testid={`condition-input-${f.key}`} />
                  <span className="text-xs text-slate-400">%</span>
                  {!readOnly && <button onClick={() => removeFactor(f.key)} title="Remove" data-testid={`condition-remove-${f.key}`} className="ml-0.5 text-slate-300 hover:text-rose-600"><X className="w-3.5 h-3.5" /></button>}
                </div>
              </div>
              <div className="text-[10px] text-slate-400 mt-1 leading-snug">{f.note}</div>
            </div>
          ))}
        </div>
      )}

      <div className="text-[11px] text-slate-500 mt-3 flex items-center justify-between flex-wrap gap-2">
        <span>Total site slowdown <span className="font-mono font-semibold text-slate-800" data-testid="condition-total">{totalSlow}%</span></span>
        <span className="text-slate-400">Slowdown stretches the schedule and adds an inefficiency premium to labor — a scope's own slowdown value overrides this project default.</span>
      </div>
    </Card>
  );
}

function TaxCard({ bid, profile, updateBid, readOnly }) {
  const override = bid.sales_tax || null;
  const [editing, setEditing] = useState(false);
  const [rate, setRate] = useState(override?.rate_pct ?? profile?.tax?.combined ?? "");
  const [source, setSource] = useState(override?.source || "");
  const shown = override?.rate_pct ?? profile?.tax?.combined;
  const where = [bid.location_county || bid.county, bid.state].filter(Boolean).join(", ");
  const save = () => {
    const r = Number(rate);
    if (!(r >= 0 && r < 30)) { toast.error("Enter a tax rate between 0 and 30%."); return; }
    if (!source.trim()) { toast.error("Say where the rate comes from (e.g. TX Comptroller, Brazoria County)."); return; }
    updateBid({ sales_tax: { rate_pct: r, source: source.trim() } });
    setEditing(false);
  };
  const clear = () => { updateBid({ sales_tax: null }); setEditing(false); setRate(profile?.tax?.combined ?? ""); setSource(""); };
  return (
    <Card className="p-4" data-testid="loc-tax">
      <div className="flex items-center justify-between gap-2 mb-1">
        <div className="flex items-center gap-2 text-slate-500 text-xs"><Percent className="w-4 h-4 text-[#003E87]" /> Sales/use tax (this job)</div>
        {!readOnly && !editing && <button onClick={() => setEditing(true)} className="text-[11px] text-[#003E87] hover:underline" data-testid="loc-tax-edit">{override ? "Change" : "Set for this bid"}</button>}
      </div>
      {editing ? (
        <div className="space-y-2" data-testid="loc-tax-editor">
          <div className="flex items-center gap-2">
            <Input type="number" step="0.01" value={rate} onChange={(e) => setRate(e.target.value)} className="h-8 w-24 text-sm font-mono text-right" data-testid="loc-tax-rate-input" />
            <span className="text-sm text-slate-500">%</span>
          </div>
          <Input value={source} onChange={(e) => setSource(e.target.value)} placeholder="Source — e.g. TX Comptroller: 6.25% state + 2% Brazoria County" className="h-8 text-xs" data-testid="loc-tax-source-input" />
          <div className="flex items-center gap-2">
            <Button size="sm" onClick={save} className="h-7 bg-[#003E87] hover:bg-[#00305F] text-xs" data-testid="loc-tax-save">Save</Button>
            <Button size="sm" variant="outline" onClick={() => setEditing(false)} className="h-7 text-xs" data-testid="loc-tax-cancel">Cancel</Button>
            {override && <button onClick={clear} className="text-[11px] text-slate-500 hover:text-rose-600 ml-auto" data-testid="loc-tax-clear">Use the location rate</button>}
          </div>
        </div>
      ) : (
        <>
          <div className="text-xl font-bold font-mono text-slate-900" data-testid="loc-tax-rate">{shown != null ? `${Number(shown).toFixed(2)}%` : "—"}</div>
          <div className="text-[10px] text-slate-400 mt-1" data-testid="loc-tax-source">
            {override ? <>Set on this bid · {override.source}</> : <>{where ? `${where}: ` : ""}state {profile?.tax?.state_rate}% + local {profile?.tax?.county_adder ?? 0}% · {profile?.tax?.source}</>}
          </div>
          <div className="text-[10px] text-slate-400 mt-1">Applied to rented equipment and taxable material; owned equipment and sub-supplied material carry none.</div>
        </>
      )}
    </Card>
  );
}

function StandDownCard({ bid, updateBid, readOnly }) {
  const blocks = bid.schedule_blocks || [];
  const commit = (next) => updateBid({ schedule_blocks: next });
  const add = () => commit([...blocks, { id: `blk_${Date.now()}`, name: "Weather stand-down", kind: "weather", work_days: 5, crew_per_diem: false }]);
  const upd = (id, k, v) => commit(blocks.map((b) => (b.id === id ? { ...b, [k]: v } : b)));
  const del = (id) => commit(blocks.filter((b) => b.id !== id));
  const total = blocks.reduce((s, b) => s + (b.enabled === false ? 0 : Number(b.work_days) || 0), 0);
  return (
    <Card className="p-5 mb-4" data-testid="standdown-card">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="flex items-center gap-2 text-sm font-semibold text-slate-700"><CloudRain className="w-4 h-4 text-[#003E87]" /> Stand-down blocks</div>
          <p className="text-[11px] text-slate-500 mt-1 max-w-2xl">Days on site with no crew production — expected weather, a plant shutdown, an owner hold. Each block is its own bar after the work: management, GC months and whole-window rentals extend; crew labor does not. Crew per-diem only if you say so.</p>
        </div>
        <div className="text-right">
          <div className="text-2xl font-bold font-mono text-[#003E87]" data-testid="standdown-total">{total}</div>
          <div className="text-[10px] text-slate-400">stand-down work-days</div>
        </div>
      </div>
      {blocks.length > 0 && (
        <div className="mt-3 space-y-2">
          {blocks.map((b) => (
            <div key={b.id} className="grid grid-cols-2 sm:grid-cols-5 gap-2 items-end rounded-lg border border-slate-200 bg-white p-3" data-testid={`standdown-block-${b.id}`}>
              <label className="block sm:col-span-2"><span className="text-[9px] uppercase tracking-wide text-slate-400">Name</span>
                <Input value={b.name || ""} disabled={readOnly} onChange={(e) => upd(b.id, "name", e.target.value)} className="h-8 text-xs mt-0.5" data-testid={`standdown-name-${b.id}`} /></label>
              <label className="block"><span className="text-[9px] uppercase tracking-wide text-slate-400">Kind</span>
                <select disabled={readOnly} value={b.kind || "weather"} onChange={(e) => upd(b.id, "kind", e.target.value)} data-testid={`standdown-kind-${b.id}`} className="w-full h-8 mt-0.5 text-xs border border-slate-200 rounded px-2 bg-white"><option value="weather">Weather</option><option value="shutdown">Plant shutdown</option><option value="hold">Owner hold</option><option value="other">Other</option></select></label>
              <label className="block"><span className="text-[9px] uppercase tracking-wide text-slate-400">Work-days</span>
                <Input type="number" step="any" value={b.work_days ?? ""} disabled={readOnly} onChange={(e) => upd(b.id, "work_days", e.target.value === "" ? "" : Number(e.target.value))} className="h-8 text-xs font-mono mt-0.5" data-testid={`standdown-days-${b.id}`} /></label>
              <div className="flex items-center justify-between gap-2 pb-1">
                <label className="flex items-center gap-1.5 text-[11px] text-slate-600"><input type="checkbox" disabled={readOnly} checked={!!b.crew_per_diem} onChange={(e) => upd(b.id, "crew_per_diem", e.target.checked)} data-testid={`standdown-crewpd-${b.id}`} /> crew per-diem</label>
                {!readOnly && <button onClick={() => del(b.id)} className="text-slate-300 hover:text-rose-600" data-testid={`standdown-remove-${b.id}`}><X className="w-4 h-4" /></button>}
              </div>
            </div>
          ))}
        </div>
      )}
      {!readOnly && <button onClick={add} className="mt-3 text-xs text-[#003E87] hover:underline" data-testid="standdown-add">＋ Add a stand-down block</button>}
    </Card>
  );
}
